import SortArrows from '@/assets/icons/chevron-selector-vertical.svg?react';
import styles from './call-logs-table.module.scss';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { Call } from '@/services/call-log-service/call-log-service.types';
import { Pagination } from '@/services/request/request.types';
import { ConnectAgent } from '../connect-agent/connect-agent';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Divider, Empty, Flex, Spin, Table, TablePaginationConfig } from 'antd';
import { ColumnsType, FilterValue, SorterResult } from 'antd/es/table/interface';
import { useNavigate, Link } from 'react-router-dom';
import TopicChip from '../topic-chip/topic-chip';
import { generateDataSource, renderCallIconWithDuration } from './call-logs-table.utils';
import { Dispatch, SetStateAction } from 'react';
import StatusChip from '../status-chip/status-chip';

interface CallLogsTableProps {
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  onSelectRecord?: (record: Call) => void;
  selectedRecord?: Call;
  callLogData?: Call[];
  pagination?: Pagination;
  setIsPlaying?: Dispatch<SetStateAction<boolean>>;
  currentPage: number;
  loading?: boolean;
  loadingRecordId?: number;
}

export const CallLogsTable = ({
  onChange,
  onSelectRecord,
  callLogData,
  pagination,
  selectedRecord,
  setIsPlaying,
  currentPage,
  loading,
  loadingRecordId,
}: CallLogsTableProps) => {
  const navigate = useNavigate();
  const dataSource = generateDataSource(callLogData || []);

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<Call> | SorterResult<Call>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    const sortBy = sort?.field?.toString();
    const orderBy = sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined;

    onChange(pagination.current, pagination.pageSize, sortBy, orderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<Call> = [
    {
      title: 'Type',
      dataIndex: 'duration',
      key: 'duration',
      sorter: true,
      className: styles.callContainer,
      width: 80,
      render: (duration: Call['duration'], record) =>
        renderCallIconWithDuration(record, duration, selectedRecord, onSelectRecord, setIsPlaying, loadingRecordId),
      sortIcon: () => (
        <Icon
          component={() => <SortArrows stroke={styles.arrowColorIcon} width={14} height={14} />}
        />
      ),
    },
    {
      title: 'Phone number',
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      render: (phone: Call['phone'], record) =>
        phone ? (
          <Flex align="center">
            {record?.language_flag_url && (
              <div className={styles.flagContainer}>
                <img src={record.language_flag_url} alt={record.language || 'Flag'} />
              </div>
            )}
            <span className={styles.phoneNumber}>
              {phone}
            </span>
          </Flex>
        ) : (
          <p className={styles.noRecords}>---</p>
        ),
    },
    {
      width: 150,
      sortIcon: () => (
        <Icon
          component={() => <SortArrows stroke={styles.arrowColorIcon} width={14} height={14} />}
        />
      ),
      title: 'Date and time',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      className: styles.dateAndTimeContainer,
      render: (created_at: Call['created_at'], record) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(created_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(created_at)}</p>
          </Flex>
          <p className={styles.callSid}>{record.call_sid}</p>
        </Flex>
      ),
    },
    {
      title: 'Topic',
      dataIndex: 'topics',
      key: 'topics',
      className: styles.topicsContainer,
      render: (topics: Call['topics']) => (
        <Flex className={styles.topicFlexContainer} wrap="wrap">
          {!topics.length ? (
            <p>---</p>
          ) : (
            [...new Set(topics)].map((topic) => (
              <TopicChip
                key={topic}
                className={styles.topicChip}
                title={topic}
                variant={topic === 'Other' ? 'secondary' : 'primary'}
              />
            ))
          )}
        </Flex>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company',
      className: styles.topicsContainer,
      render: (company_name: Call['company_name']) => <div>{company_name || ''}</div>,
    },
    {
      title: 'Summary',
      dataIndex: 'convo_summary',
      key: 'convo_summary',
      className: styles.summaryContainer,
      render: (summary: Call['convo_summary'], record) => (
        <div>
          {!summary ? <p>---</p> : <p>{summary}</p>}
          {record.sms_summary_message && (
            <>
              <Divider dashed />
              <p className={styles.smsSummaryMessage}>SMS summary message has been sent.</p>
            </>
          )}
        </div>
      ),
    },
    {
      width: 100,
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      className: styles.statusContainer,
      render: (status: Call['status']) => (
        <StatusChip className={styles.statusChip} variant={status || 'unresolved'} />
      ),
    },
    {
      title: 'Connect Agent',
      dataIndex: 'agent',
      key: 'agent',
      className: styles.agentContainer,
      render: (agent: Call['agent'], record) => {
        const type: 'outgoing' | 'agentCall' | 'unanswered' =
          agent && record.type === 'AnalyticsCall'
            ? 'outgoing'
            : agent && record.type === 'AgentCall'
              ? 'agentCall'
              : 'unanswered';

        return (
          <>
            {record?.related_call ? (
              <Link to={`/conversations/${record?.related_call}`} className={styles.phoneNumber}>
                <ConnectAgent type={type} />
              </Link>
            ) : (
              <ConnectAgent type={type} />
            )}
          </>
        );
      },
    },
  ];

  return (
    <Table
      onRow={(record) => ({
        onClick: () => navigate(`/conversations/${record.id}`),
        style: { cursor: 'pointer' },
      })}
      pagination={{
        current: currentPage,
        position: ['bottomCenter'],
        pageSizeOptions: ['12', '25', '50'],
        showSizeChanger: true,
        defaultPageSize: pagination?.pageItems,
        total: pagination?.totalCount,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      onChange={handleChange}
      scroll={{ x: 800 }}
      className={styles.table}
      dataSource={dataSource}
      columns={columns}
      locale={{
        emptyText: loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
};
